import { useCallback } from 'react';

/**
 * Generic dropdown handlers hook
 * Consolidates common dropdown handler logic to avoid duplication
 * Works with reducer-based state management
 * 
 * @param {Object} config - Configuration object
 * @param {Array} config.items - Array of items to select from
 * @param {Array} config.selectedItems - Currently selected items (from state)
 * @param {Function} config.setSelectedItems - Function to update selected items
 * @param {Function} config.setSelectAll - Function to update selectAll state
 * @param {boolean} config.selectAll - Current selectAll state
 * @param {Function} config.getItemId - Function to extract ID from item (default: item => item._id)
 * @param {Function} config.setCurrentPage - Function to set current page (optional)
 * @returns {Object} Dropdown handlers
 */
export const useDropdownHandlers = ({
  items = [],
  selectedItems = [],
  setSelectedItems,
  setSelectAll,
  selectAll = false,
  getItemId = (item) => item._id,
  setCurrentPage,
}) => {
  // Handle individual checkbox change
  const handleCheckboxChange = useCallback((itemId) => {
    const updatedSelection = [...selectedItems];
    const index = updatedSelection.indexOf(itemId);

    if (index === -1) {
      // Add to selection
      updatedSelection.push(itemId);
    } else {
      // Remove from selection
      updatedSelection.splice(index, 1);
    }

    // Reset page if configured
    if (setCurrentPage) {
      setCurrentPage(1);
    }

    setSelectedItems(updatedSelection);
  }, [selectedItems, setSelectedItems, setCurrentPage]);

  // Handle select all change
  const handleSelectAllChange = useCallback(() => {
    const allItemIds = items.map(getItemId);
    const newSelection = selectAll ? [] : allItemIds;

    // Reset page if configured
    if (setCurrentPage) {
      setCurrentPage(1);
    }

    setSelectedItems(newSelection);
    setSelectAll(!selectAll);
  }, [selectAll, items, getItemId, setSelectedItems, setSelectAll, setCurrentPage]);

  return {
    handleCheckboxChange,
    handleSelectAllChange,
  };
};

/**
 * Generic toggle dropdown handler
 * Consolidates toggle logic for dropdowns
 * 
 * @param {Function} toggleAction - Redux action to toggle dropdown
 * @param {Function} clearSearchTerm - Optional function to clear search term
 * @returns {Function} Toggle handler function
 */
export const useToggleDropdown = (toggleAction, clearSearchTerm) => {
  return useCallback(() => {
    toggleAction();
    if (clearSearchTerm) {
      clearSearchTerm("");
    }
  }, [toggleAction, clearSearchTerm]);
};

